import { inject, Injectable } from '@angular/core';
import {
    APP_CONFIG_TOKEN,
    DROPBOX_OAUTH_CALLBACK_SOURCE,
    DROPBOX_OAUTH_CALLBACK_STORAGE_KEY,
    type DropboxOAuthCallbackMessage,
} from '@quipex/core/config';
import {
    normalizeToProblemDetails,
    type ResolvedProblemDetails,
} from '@quipex/core/utils';
import { DROPBOX_AUTH_ERROR } from '../error/error-codes';
import { type BuildUrnParts, injectError } from '../error/inject-error';

const DROPBOX_AUTHORIZE_URL = 'https://www.dropbox.com/oauth2/authorize';
const DROPBOX_TOKEN_URL = 'https://api.dropboxapi.com/oauth2/token';
const CALLBACK_PATH = '/auth/dropbox/callback';
const CALLBACK_TIMEOUT_MS = 5 * 60 * 1000;

interface DropboxTokenResponse {
    access_token: string;
    expires_in: number;
    token_type: string;
    account_id?: string;
    uid?: string;
}

interface PendingDropboxAuth {
    state: string;
    codeVerifier: string;
}

@Injectable({
    providedIn: 'root',
})
export class DropboxAuthService {
    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    private readonly clientError = injectError();
    private readonly loggingId: BuildUrnParts = ['service', 'dropbox-auth'];
    private accessToken: string | null = null;
    private accessTokenExpiresAtMs: number | null = null;

    public hasToken(): boolean {
        return (
            !!this.accessToken &&
            !!this.accessTokenExpiresAtMs &&
            Date.now() < this.accessTokenExpiresAtMs
        );
    }

    public getToken(): string | null {
        if (!this.hasToken()) {
            this.clearToken();
            return null;
        }

        return this.accessToken;
    }

    public async connect(): Promise<string> {
        const cachedToken = this.getToken();
        if (cachedToken) return cachedToken;

        const pending = await this.startAuthorization();
        const message = await this.waitForCallback(pending);

        if (message.error) {
            throw this.createDropboxError(
                DROPBOX_AUTH_ERROR.AuthorizationFailed,
                'Dropbox connection failed',
                message.errorDescription ?? message.error
            );
        }

        if (!message.code || message.state !== pending.state) {
            throw this.createDropboxError(
                DROPBOX_AUTH_ERROR.AuthorizationFailed,
                'Dropbox connection failed',
                'Dropbox did not return a valid authorization response.'
            );
        }

        const token = await this.exchangeCodeForToken(
            message.code,
            pending.codeVerifier
        );
        this.accessToken = token.access_token;
        this.accessTokenExpiresAtMs = Date.now() + token.expires_in * 1000;
        return this.accessToken;
    }

    private async startAuthorization(): Promise<PendingDropboxAuth> {
        const clientId = this.appConfig.dropboxClientId?.trim();
        if (!clientId) {
            throw this.createDropboxError(
                DROPBOX_AUTH_ERROR.ConfigurationInvalid,
                'Dropbox is not configured',
                'Dropbox browser access is not configured for this environment.'
            );
        }

        const state = this.createRandomToken();
        const codeVerifier = this.createRandomToken();
        const codeChallenge = await this.createCodeChallenge(codeVerifier);
        const redirectUri = this.getRedirectUri();
        const url = new URL(DROPBOX_AUTHORIZE_URL);
        url.searchParams.set('client_id', clientId);
        url.searchParams.set('response_type', 'code');
        url.searchParams.set('redirect_uri', redirectUri);
        url.searchParams.set('state', state);
        url.searchParams.set('token_access_type', 'online');
        url.searchParams.set('code_challenge', codeChallenge);
        url.searchParams.set('code_challenge_method', 'S256');

        const popup = window.open(
            url.toString(),
            'quipex-dropbox-oauth',
            'popup,width=520,height=720'
        );

        if (!popup) {
            throw this.createDropboxError(
                DROPBOX_AUTH_ERROR.PopupBlocked,
                'Dropbox connection blocked',
                'The Dropbox connection window was blocked by the browser.'
            );
        }

        return {
            state,
            codeVerifier,
        };
    }

    private waitForCallback(
        pending: PendingDropboxAuth
    ): Promise<DropboxOAuthCallbackMessage> {
        return new Promise((resolve, reject) => {
            const resolveMessage = (message: DropboxOAuthCallbackMessage) => {
                cleanup();
                this.clearStoredCallbackMessage();
                resolve(message);
            };
            const tryResolveMessage = (
                message: DropboxOAuthCallbackMessage | null
            ): boolean => {
                if (!message || message.state !== pending.state) return false;

                resolveMessage(message);
                return true;
            };
            const onMessage = (event: MessageEvent<unknown>) => {
                if (event.origin !== window.location.origin) return;
                if (!this.isDropboxCallbackMessage(event.data)) return;

                tryResolveMessage(event.data);
            };
            const onStorage = (event: StorageEvent) => {
                if (event.key !== DROPBOX_OAUTH_CALLBACK_STORAGE_KEY) return;

                tryResolveMessage(this.parseCallbackMessage(event.newValue));
            };
            const startedAt = Date.now();
            const intervalId = window.setInterval(() => {
                if (tryResolveMessage(this.readStoredCallbackMessage())) {
                    return;
                }

                if (Date.now() - startedAt < CALLBACK_TIMEOUT_MS) return;

                cleanup();
                reject(
                    this.createDropboxError(
                        DROPBOX_AUTH_ERROR.AuthorizationTimedOut,
                        'Dropbox connection timed out',
                        'Dropbox did not complete authorization in time. Try connecting again.'
                    )
                );
            }, 500);
            const cleanup = () => {
                window.removeEventListener('message', onMessage);
                window.removeEventListener('storage', onStorage);
                window.clearInterval(intervalId);
            };

            window.addEventListener('message', onMessage);
            window.addEventListener('storage', onStorage);
        });
    }

    private clearToken(): void {
        this.accessToken = null;
        this.accessTokenExpiresAtMs = null;
    }

    private readStoredCallbackMessage(): DropboxOAuthCallbackMessage | null {
        try {
            return this.parseCallbackMessage(
                window.localStorage.getItem(DROPBOX_OAUTH_CALLBACK_STORAGE_KEY)
            );
        } catch {
            return null;
        }
    }

    private clearStoredCallbackMessage(): void {
        try {
            window.localStorage.removeItem(DROPBOX_OAUTH_CALLBACK_STORAGE_KEY);
        } catch {
            // Best-effort cleanup only.
        }
    }

    private async exchangeCodeForToken(
        code: string,
        codeVerifier: string
    ): Promise<DropboxTokenResponse> {
        const body = new URLSearchParams({
            code,
            grant_type: 'authorization_code',
            client_id: this.appConfig.dropboxClientId,
            code_verifier: codeVerifier,
            redirect_uri: this.getRedirectUri(),
        });
        const response = await fetch(DROPBOX_TOKEN_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body,
        });

        if (!response.ok) {
            throw this.createDropboxError(
                DROPBOX_AUTH_ERROR.TokenExchangeFailed,
                'Dropbox token exchange failed',
                await this.readErrorDetail(response),
                {
                    responseStatus: response.status,
                    responseStatusText: response.statusText,
                },
                [response]
            );
        }

        return (await response.json()) as DropboxTokenResponse;
    }

    private getRedirectUri(): string {
        return new URL(CALLBACK_PATH, window.location.origin).toString();
    }

    private createRandomToken(): string {
        const bytes = new Uint8Array(32);
        window.crypto.getRandomValues(bytes);
        return this.toBase64Url(bytes);
    }

    private async createCodeChallenge(codeVerifier: string): Promise<string> {
        const data = new TextEncoder().encode(codeVerifier);
        const digest = await window.crypto.subtle.digest('SHA-256', data);
        return this.toBase64Url(new Uint8Array(digest));
    }

    private toBase64Url(bytes: Uint8Array): string {
        let value = '';
        for (const byte of bytes) {
            value += String.fromCharCode(byte);
        }

        return window
            .btoa(value)
            .replaceAll('+', '-')
            .replaceAll('/', '_')
            .replaceAll('=', '');
    }

    private isDropboxCallbackMessage(
        value: unknown
    ): value is DropboxOAuthCallbackMessage {
        return (
            typeof value === 'object' &&
            value !== null &&
            'source' in value &&
            value.source === DROPBOX_OAUTH_CALLBACK_SOURCE
        );
    }

    private parseCallbackMessage(
        value: string | null
    ): DropboxOAuthCallbackMessage | null {
        if (!value) return null;

        try {
            const parsed = JSON.parse(value) as unknown;
            return this.isDropboxCallbackMessage(parsed) ? parsed : null;
        } catch {
            return null;
        }
    }

    private async readErrorDetail(response: Response): Promise<string> {
        try {
            return await response.text();
        } catch {
            return `${response.status} ${response.statusText}`;
        }
    }

    private createDropboxError(
        spec: (typeof DROPBOX_AUTH_ERROR)[keyof typeof DROPBOX_AUTH_ERROR],
        title: string,
        detail: string,
        context?: Record<string, unknown>,
        consoleErrorRestArgs?: readonly unknown[]
    ): ResolvedProblemDetails {
        const error = this.clientError.create(spec, {
            urnParts: [...this.loggingId, spec.statusText] as BuildUrnParts,
            context,
            consoleErrorRestArgs,
        });

        return {
            ...normalizeToProblemDetails(error),
            title,
            detail,
        };
    }
}
